import React, { useEffect, useMemo, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { Input } from "@/components/ui/input";
import { Search, MapPin, GraduationCap, Home, ExternalLink } from "lucide-react";
import { CITY_META } from "@/lib/cityMeta";
import { api } from "@/lib/api";

const GOOGLE_LIBRARIES = ["places"];

// Build a searchable index from cities + coaching centers + neighborhoods.
// Extended dynamically with fetched hostels so hostel names + addresses are also matchable.
function buildStaticIndex() {
  const items = [];
  for (const [city, meta] of Object.entries(CITY_META)) {
    items.push({ type: "city", label: city, sub: meta.tagline, slug: meta.slug, city, kind: "location" });
    (meta.coaching_hubs || []).forEach((c) =>
      items.push({ type: "coaching", label: c, sub: `Coaching · ${city}`, city, coaching: c, kind: "coaching" })
    );
    (meta.neighborhoods || []).forEach((n) =>
      items.push({ type: "area", label: n, sub: `Area · ${city}`, city, kind: "location" })
    );
  }
  return items;
}

const STATIC_INDEX = buildStaticIndex();

export default function LocationAutocomplete({ value = "", onSelect, placeholder = "Type a city, coaching or area…", autoFocus = false }) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [placesPredictions, setPlacesPredictions] = useState([]);
  const wrapRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Load Google Places (only if the API key is present — falls back silently
  // to static + hostel matches otherwise).
  const { isLoaded: gmapsReady } = useJsApiLoader({
    id: "google-maps-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_LIBRARIES,
  });

  // Lazy load hostels once for name/address matches
  useEffect(() => {
    api.get("/hostels").then((r) => setHostels(r.data)).catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Fetch Google Places (New API) predictions as the user types (debounced 220 ms).
  // India-restricted so we don't waste quota on irrelevant international hits.
  // Uses the *new* AutocompleteSuggestion API (mandatory for Google Cloud
  // projects created after 1 March 2025). Legacy AutocompleteService is not
  // available on new projects.
  useEffect(() => {
    if (!gmapsReady || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setPlacesPredictions([]);
      return;
    }
    if (!q || q.trim().length < 2) { setPlacesPredictions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { suggestions } = await window.google.maps.places
          .AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: q,
            includedRegionCodes: ["in"],
          });
        const norm = (suggestions || []).slice(0, 4).map((s) => {
          const p = s.placePrediction;
          return {
            place_id: p?.placeId,
            description: p?.text?.text || "",
            main_text: p?.mainText?.text || p?.text?.text || "",
            secondary_text: p?.secondaryText?.text || "",
          };
        });
        setPlacesPredictions(norm);
      } catch (e) {
        // API not enabled / referrer blocked / quota exhausted — fall back to
        // static + hostel matches silently.
        setPlacesPredictions([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, gmapsReady]);

  const results = useMemo(() => {
    if (!q || q.length < 1) return [];
    const term = q.toLowerCase();
    // Static suggestions
    const staticMatches = STATIC_INDEX.filter((i) => i.label.toLowerCase().includes(term)).slice(0, 8);
    // Hostel matches (name / city — address is intentionally stripped from public API for privacy)
    const hostelMatches = hostels
      .filter((h) => {
        const name = (h.name || "").toLowerCase();
        const city = (h.city || "").toLowerCase();
        return name.includes(term) || city.includes(term);
      })
      .slice(0, 4)
      .map((h) => ({ type: "hostel", label: h.name, sub: `${h.city}${h.coaching_centers?.[0] ? ` · near ${h.coaching_centers[0]}` : ""}`, city: h.city, hostel_id: h.id, kind: "hostel" }));
    return [...hostelMatches, ...staticMatches].slice(0, 10);
  }, [q, hostels]);

  const gmapsLink = q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q + " India")}` : null;

  const choose = (item) => {
    onSelect?.(item);
    setQ(item.label);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[activeIndex]) { e.preventDefault(); choose(results[activeIndex]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const iconFor = (t) =>
    t === "city" ? MapPin : t === "coaching" ? GraduationCap : t === "hostel" ? Home : MapPin;

  return (
    <div ref={wrapRef} className="relative flex-1">
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 focus-within:bg-slate-50 transition-colors">
        <Search size={16} className="text-orange-800 shrink-0" />
        <Input
          data-testid="location-autocomplete-input"
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIndex(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="border-0 shadow-none focus-visible:ring-0 h-9 px-0"
        />
      </div>

      {open && q && (
        <div data-testid="location-suggestions" className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden z-30 max-h-80 overflow-y-auto">
          {results.length === 0 && placesPredictions.length === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center">
              No local matches. Try searching directly on Google Maps →
            </div>
          )}
          {results.map((r, i) => {
            const Icon = iconFor(r.type);
            const active = i === activeIndex;
            return (
              <button
                key={`${r.type}-${r.label}-${i}`}
                type="button"
                onClick={() => choose(r)}
                onMouseEnter={() => setActiveIndex(i)}
                data-testid={`suggestion-${r.type}-${i}`}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${active ? "bg-orange-50" : "hover:bg-slate-50"}`}
              >
                <Icon size={16} className={r.type === "hostel" ? "text-emerald-600" : r.type === "coaching" ? "text-amber-600" : "text-orange-800"} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.label}</div>
                  <div className="text-xs text-slate-500 truncate">{r.sub}</div>
                </div>
                <span className="text-[10px] tracking-widest uppercase font-bold text-slate-400">{r.type}</span>
              </button>
            );
          })}
          {gmapsLink && placesPredictions.length === 0 && (
            <a
              href={gmapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-in-google-maps"
              className="block border-t border-slate-100 bg-slate-50 hover:bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800 flex items-center justify-between"
            >
              <span className="flex items-center gap-2"><MapPin size={14} /> View "{q}" on Google Maps</span>
              <ExternalLink size={12} />
            </a>
          )}
          {placesPredictions.length > 0 && (
            <div className="border-t border-slate-100">
              <div className="px-4 py-1.5 text-[9px] tracking-widest uppercase font-bold text-slate-400 bg-slate-50">From Google Places</div>
              {placesPredictions.map((p, i) => (
                <button
                  key={p.place_id || i}
                  type="button"
                  data-testid={`suggestion-places-${i}`}
                  onClick={() => choose({
                    type: "place",
                    label: p.main_text || p.description,
                    sub: p.secondary_text || "",
                    city: "",
                    kind: "location",
                    place_id: p.place_id,
                  })}
                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50"
                >
                  <MapPin size={16} className="text-sky-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{p.main_text || p.description}</div>
                    <div className="text-xs text-slate-500 truncate">{p.secondary_text}</div>
                  </div>
                  <span className="text-[10px] tracking-widest uppercase font-bold text-slate-400">place</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
